/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { createContext, useContext } from "react"
import { mapValues } from 'lodash'
import forEachObjIndexed from "ramda/src/forEachObjIndexed"
import { EMPTY } from "../constants/misc"

type LocaleContextKey =
  | "locale"
  | "localeContent"
  | "switchLocale"
type SwitchLocaleCallback = (
  locale: string,
) => void
export interface LocaleContent {
  [idx: string]: string | LocaleContent
}

export const LocaleContext = createContext<{
  locale: string
  localeContent: LocaleContent
  switchLocale: SwitchLocaleCallback
}>({
  locale: "en",
  localeContent: EMPTY,
  switchLocale: () => {},
})

export function useLocaleContext(
  key: "locale",
): string
export function useLocaleContext(
  key: "localeContent",
): LocaleContent
export function useLocaleContext(
  key: "switchLocale",
): SwitchLocaleCallback
export function useLocaleContext(): [
  string,
  LocaleContent,
  SwitchLocaleCallback,
]
export function useLocaleContext(
  key?: LocaleContextKey,
):
  | string
  | LocaleContent
  | SwitchLocaleCallback
  | [
      string,
      LocaleContent,
      SwitchLocaleCallback,
    ] {
  const localeContext = useContext(LocaleContext)
  if (key === "locale")
    return localeContext.locale
  if (key === "localeContent")
    return localeContext.localeContent
  if (key === "switchLocale")
    return localeContext.switchLocale
  return [
    localeContext.locale,
    localeContext.localeContent,
    localeContext.switchLocale,
  ]
}

export const translate = (
  localeContent: LocaleContent,
  key: string,
  replacement?: { [idx: string]: string },
): string => {
  if (localeContent === EMPTY || !key) return ""
  const splitKeys = key.split(".")
  let res: string | LocaleContent = localeContent
  let notContent = false
  for (
    let idx = 0;
    idx < splitKeys.length;
    idx += 1
  ) {
    const splitKey = splitKeys[idx]
    if (typeof res === "string") {
      notContent = true
      break
    }
    if (res[splitKey] === undefined) {
      notContent = true
      break
    }
    res = res[splitKey]
  }
  if (notContent || typeof res !== "string") {
    if (process.env.NODE_ENV !== "production") {
      return "CONTENT NOT FOUND"
    }
    return ""
  }
  if (!replacement) return res
  forEachObjIndexed(
    (replacementVal, replacementKey) => {
      res = (res as string).replace(
        `${replacementKey}`,
        replacementVal,
      )
    },
    replacement,
  )
  return res
}

export const translateMultiple = (
  localeContent: LocaleContent,
  keys: Array<
    string | [string, { [idx: string]: string }]
  >,
): Array<string> => {
  const result = []
  for (let idx = 0; idx < keys.length; idx += 1) {
    const key = keys[idx]
    if (typeof key === "string") {
      result.push(translate(localeContent, key))
      continue
    }
    result.push(translate(localeContent, ...key))
  }
  return result
}

export const translateMultipleMap = (
  localeContent: LocaleContent,
  keys: {
    [key: string]: string
  },
): {
  [key: string]: string
} =>  mapValues(keys,key=>translate(localeContent, key))

export const useTranslation = (
  key: string,
  replacement?: { [idx: string]: string },
): string => {
  const localeContent = useLocaleContext(
    "localeContent",
  )
  return translate(
    localeContent,
    key,
    replacement,
  )
}

export const useTranslations = (
  keys: Array<
    string | [string, { [idx: string]: string }]
  >,
): Array<string> => {
  const localeContent = useLocaleContext(
    "localeContent",
  )
  return translateMultiple(localeContent, keys)
}

export const useTranslationsMap = (
  keys: {
    [key: string]: string
  },
):{
  [key: string]: string
} => {
  const localeContent = useLocaleContext(
    "localeContent",
  )
  return translateMultipleMap(localeContent, keys)
}
